// components/EventCategories.js - Event categories component with Redux integration
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Typography, Grid, Card, CardContent, Chip, alpha } from '@mui/material';
import {
  Business as BusinessIcon,
  Favorite as FavoriteIcon,
  Cake as CakeIcon,
  Groups as GroupsIcon,
  RocketLaunch as RocketLaunchIcon,
  MusicNote as MusicNoteIcon,
} from '@mui/icons-material';

const EventCategories = () => {
  const dispatch = useDispatch();
  const { categories, selectedCategory } = useSelector((state) => state.eventCategories);

  const iconMap = {
    Business: <BusinessIcon />,
    Favorite: <FavoriteIcon />,
    Cake: <CakeIcon />,
    Groups: <GroupsIcon />,
    RocketLaunch: <RocketLaunchIcon />,
    MusicNote: <MusicNoteIcon />,
  };

  const handleCategoryClick = (categoryId) => {
    dispatch((categoryId));
  };

  return (
    <Box id="categories" sx={{ py: 8 }}>
      <Typography
        variant="h2"
        align="center"
        sx={{ mb: 2, background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}
      >
        Event Categories
      </Typography>
      <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6, maxWidth: 700, mx: 'auto' }}>
        We specialize in managing a wide range of events, each tailored to your unique needs
      </Typography>

      <Grid container spacing={3}>
        {categories.map((category) => (
          <Grid item xs={12} sm={6} md={4} key={category.id}>
            <Card
              onClick={() => handleCategoryClick(category.id)}
              sx={{
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.3s',
                borderRadius: 3,
                overflow: 'hidden',
                border: selectedCategory === category.id ? 2 : 1,
                borderColor: selectedCategory === category.id ? category.color : 'divider',
                boxShadow: selectedCategory === category.id ? 4 : 1,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6,
                },
              }}
            >
              <Box
                sx={{
                  height: 120,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(category.color, 0.1),
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 70,
                    height: 70,
                    borderRadius: '50%',
                    bgcolor: category.color,
                    color: 'white',
                  }}
                >
                  {iconMap[category.icon] || <BusinessIcon />}
                </Box>
              </Box>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                  {category.name}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                  <Chip
                    label={`${category.count} Events`}
                    size="small"
                    sx={{ bgcolor: alpha(category.color, 0.1), color: category.color }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {selectedCategory && (
        <Box
          sx={{
            mt: 6,
            p: 4,
            borderRadius: 3,
            bgcolor: alpha('#6a11cb', 0.05),
            borderLeft: 6,
            borderColor: 'primary.main',
          }}
        >
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              component="span"
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: 14,
                mr: 2,
              }}
            >
              ✓
            </Box>
            {categories.find(c => c.id === selectedCategory)?.name} Selected
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Great choice! Our team has extensive experience in {categories.find(c => c.id === selectedCategory)?.name.toLowerCase()}. 
            Share your requirements in the contact form below, and we'll prepare a customized proposal for you.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default EventCategories;